class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.isFetchingOnHover = false;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );

    // Show cart notification on hover of the bag icon
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (cartIcon) {
      cartIcon.addEventListener('mouseenter', this.openOnHover.bind(this));
    }
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  async openOnHover() {
    try {
      if (this.isFetchingOnHover) return;
      this.isFetchingOnHover = true;

      // Fetch latest sections HTML for the cart notification, including subtotal
      const sections = ['cart-notification-product', 'cart-notification-button', 'cart-notification-subtotal', 'cart-icon-bubble'];
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;
      const response = await fetch(url, { credentials: 'same-origin' });
      if (response.ok) {
        const sectionsHtml = await response.json();
        sections.forEach((id) => {
          const container = document.getElementById(id);
          if (!container) return;
          if (id === 'cart-notification-subtotal') {
            // Update the subtotal section
            const subtotalContainer = this.querySelector('.cart-notification__subtotal');
            if (subtotalContainer) {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = sectionsHtml[id];
              const newSubtotal = tempDiv.querySelector('.cart-notification__subtotal');
              if (newSubtotal) {
                subtotalContainer.innerHTML = newSubtotal.innerHTML;
              }
            }
          } else {
            container.innerHTML = sectionsHtml[id];
          }
        });
        
        // Re-initialize MetalliCSS on newly inserted buttons
        this.initializeMetalliCSS();
      }

      // Reveal header if needed and open the notification
      if (this.header) this.header.reveal();
      this.open();

      // Change heading text for hover context (not "Item added ...")
      const heading = this.querySelector('.cart-notification__heading');
      if (heading) {
        heading.textContent = 'Your bag';
      }

      // Close when mouse leaves the panel area
      const closeOnLeave = (evt) => {
        const toElement = evt.relatedTarget;
        if (!toElement || !this.contains(toElement)) {
          this.close();
          this.removeEventListener('mouseleave', closeOnLeave);
        }
      };
      this.addEventListener('mouseleave', closeOnLeave);
    } catch (e) {
      console.error('Failed to open cart notification on hover', e);
    } finally {
      this.isFetchingOnHover = false;
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-notification-subtotal') {
        // Handle subtotal section separately - update it directly from the section HTML
        if (parsedState.sections && parsedState.sections[section.id]) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = parsedState.sections[section.id];
          // Shopify sections are wrapped in .shopify-section
          const sectionWrapper = tempDiv.querySelector('.shopify-section');
          const newSubtotal = sectionWrapper 
            ? sectionWrapper.querySelector('.cart-notification__subtotal') 
            : tempDiv.querySelector('.cart-notification__subtotal');
          
          if (newSubtotal) {
            const subtotalContainer = this.querySelector('.cart-notification__subtotal');
            if (subtotalContainer) {
              subtotalContainer.innerHTML = newSubtotal.innerHTML;
            }
          }
        }
      } else {
        const sectionElement = document.getElementById(section.id);
        if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );
        }
      }
    });

    // Update subtotal directly from response - this is the primary method
    // The response from cart/add.js includes total_price at the root level
    const totalPrice = parsedState.total_price;
    if (totalPrice !== undefined && totalPrice !== null) {
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        this.updateSubtotal(totalPrice);
      }, 0);
    }

    // Re-initialize MetalliCSS on newly inserted buttons
    this.initializeMetalliCSS();

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-notification-subtotal',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  initializeMetalliCSS() {
    // Re-initialize MetalliCSS on checkout button and add enhanced class
    setTimeout(() => {
      if (typeof window.MetalliCSS !== 'undefined' && window.MetalliCSS.init) {
        window.MetalliCSS.init();
        
        // Add enhanced class to show metallic effect
        setTimeout(() => {
          const metallicButtons = this.querySelectorAll('.metallicss');
          metallicButtons.forEach(button => {
            button.classList.add('metalli-enhanced');
          });
        }, 100);
      }
    }, 200);
  }

  updateSubtotal(totalPrice) {
    // Update the subtotal value when cart is updated
    // Try multiple selectors in case the structure varies
    const subtotalElement = this.querySelector('.cart-notification__subtotal-value') || 
                           this.notification?.querySelector('.cart-notification__subtotal-value');
    
    if (subtotalElement && totalPrice !== undefined && totalPrice !== null) {
      const formattedPrice = this.formatMoney(totalPrice);
      subtotalElement.textContent = formattedPrice;
    }
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents);
    }
    
    // Fallback formatter
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return formatter.format(cents / 100);
  }
}

customElements.define('cart-notification', CartNotification);
